import { MeasureSystem } from 'api-validations/shared'
import type { FC } from 'react'
import { ButtonKind, CircleSpinner, Icon, PanoramaButton } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'

import type { CreatePlan, ImportSummary } from '../../lib/planUpload/planUploadReducer'
import { ReadActivity } from '../../lib/planUpload/planUploadReducer'
import { BinaryChoice } from './BinaryChoice'

const BAND_CLASSES =
  'm-2 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 px-4 py-3'

const statusClasses = createVariants({
  base: 'pan-body-small flex items-center gap-2',
  variants: {
    tone: {
      pending: 'text-gray-600',
      ready: 'text-gray-700'
    }
  }
})

const SettledCounts: FC<{ imported: number; failed: number }> = ({ imported, failed }) => {
  const { t } = useLocalization()

  return (
    <>
      <span className="pan-body-bold text-green-700">
        {t(($) => $.planning.upload.imported, { count: imported })}
      </span>
      {failed > 0 && (
        <>
          <span aria-hidden>·</span>
          <span className="pan-body-bold text-red-600">
            {t(($) => $.planning.upload.importFailed, { count: failed })}
          </span>
        </>
      )}
    </>
  )
}

const PendingCounts: FC<{ included: number; newFields: number }> = ({ included, newFields }) => {
  const { t } = useLocalization()

  return (
    <>
      <span>{t(($) => $.planning.upload.included, { count: included })}</span>
      {newFields > 0 && (
        <span className="text-gray-500">
          · {t(($) => $.planning.upload.willCreateFields, { count: newFields })}
        </span>
      )}
    </>
  )
}

/**
 * Explains why an import is currently blocked, or if it is ready.
 */
const ReviewStatus: FC<{ createPlan: CreatePlan; activity: ReadActivity; locked: boolean }> = ({
  createPlan,
  activity,
  locked
}) => {
  const { t } = useLocalization()

  if (locked) return null

  if (activity !== ReadActivity.Idle)
    return (
      <span className={statusClasses({ tone: 'pending' })}>
        <CircleSpinner size="size-4" />
        {t(($) => $.planning.upload[activity])}
      </span>
    )

  if (!createPlan.ready)
    return (
      <span className={statusClasses({ tone: 'pending' })}>
        <Icon icon={SpriteMap.InfoIcon} className="size-4 shrink-0 text-gray-400" />
        {t(($) => $.planning.upload.blocked[createPlan.block])}
      </span>
    )

  return (
    <span className={statusClasses({ tone: 'ready' })}>
      <Icon icon={SpriteMap.CheckCircleIcon} className="size-4 shrink-0 text-green-600" />
      {t(($) => $.planning.upload.ready)}
    </span>
  )
}

/**
 * Binary input to declare the units of a shapefile's rate column. NOT a display setting.
 * Rows always show rates in the org's measure system either way.
 */
const RatesIn: FC<{
  measureSystem: MeasureSystem
  disabled: boolean
  onChange: (next: MeasureSystem) => void
}> = ({ measureSystem, disabled, onChange }) => {
  const { t } = useLocalization()

  return (
    <span className="flex items-center gap-2">
      <span className="pan-body-small text-gray-600">{t(($) => $.planning.upload.ratesIn)}</span>
      <BinaryChoice
        label={t(($) => $.planning.upload.shapefileRatesIn)}
        value={measureSystem}
        disabled={disabled}
        options={[
          { value: MeasureSystem.Imperial, label: t(($) => $.labels.imperial) },
          { value: MeasureSystem.Metric, label: t(($) => $.labels.metric) }
        ]}
        onChange={onChange}
      />
    </span>
  )
}

type Props = {
  summary: ImportSummary
  createPlan: CreatePlan
  activity: ReadActivity
  /**
   * Whether an import is underway.
   */
  locked: boolean
  /**
   * Present only when the upload holds shapefile rates.
   */
  rates?: { measureSystem: MeasureSystem; onChange: (next: MeasureSystem) => void }
  onClear: () => void
  onImport: () => void
}

/**
 * Summarizes the review and offers action buttons.
 */
export const ImportBand: FC<Props> = ({
  summary,
  createPlan,
  activity,
  locked,
  rates,
  onClear,
  onImport
}) => {
  const { t } = useLocalization()

  const underway = { when: locked, reason: t(($) => $.disabledReasons.actionInProgress) }
  const blocked = createPlan.ready
    ? []
    : [{ when: true, reason: t(($) => $.planning.upload.blocked[createPlan.block]) }]

  return (
    <div className={BAND_CLASSES}>
      <div className="pan-body flex flex-wrap items-baseline gap-x-1 text-gray-700">
        <span className="pan-body-bold">
          {t(($) => $.planning.upload.found, { count: summary.found })}
        </span>
        <span className="text-gray-500">
          {t(($) => $.planning.upload.inFiles, { count: summary.sources })}
        </span>
        <span aria-hidden>·</span>
        {summary.settled ? (
          <SettledCounts imported={summary.imported} failed={summary.failed} />
        ) : (
          <PendingCounts included={summary.included} newFields={summary.newFields} />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
        <ReviewStatus createPlan={createPlan} activity={activity} locked={locked} />

        {/* Locked while performing a read using the current units. */}
        {!summary.settled && rates !== undefined && (
          <RatesIn {...rates} disabled={locked || activity !== ReadActivity.Idle} />
        )}

        {summary.settled ? (
          <PanoramaButton kind={ButtonKind.Secondary} onClick={onClear}>
            {t(($) => $.planning.upload.importMore)}
          </PanoramaButton>
        ) : (
          <div className="flex items-center gap-2">
            <PanoramaButton kind={ButtonKind.White} disabledWhen={underway} onClick={onClear}>
              {t(($) => $.buttons.clear)}
            </PanoramaButton>
            <PanoramaButton
              icon={SpriteMap.CheckCircleIcon}
              disabledWhen={[underway, ...blocked]}
              onClick={onImport}
            >
              {t(($) => $.buttons.import)}
            </PanoramaButton>
          </div>
        )}
      </div>
    </div>
  )
}
